import { CheckCircle2, CircleAlert, Undo2, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { ToastContext, type ToastApi, type ToastInput } from './toastContext';
import { Kbd } from './Kbd';

/*
  Toast — короткое сообщение о том, что уже сделано, с возможностью передумать.
  Мгновенное действие (в архив, отложить, удалить копию) выполняется сразу, без вопроса, а тост
  несколько секунд держит «Отменить» (то же — Ctrl+Z). Полоска внизу показывает, сколько
  осталось; наведение мыши останавливает отсчёт. Слой z-toast, снизу по центру холста.
*/

interface Item extends ToastInput {
  id: number;
  createdAt: number;
}

const DEFAULT_MS = 5000;
const UNDO_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((list) => list.filter((t) => t.id !== id)), []);

  const api = useMemo<ToastApi>(
    () => ({
      toast: (t) => {
        seq.current += 1;
        const id = seq.current;
        // Не больше трёх сразу: старые уступают место новым.
        setItems((list) => [...list.slice(-2), { ...t, id, createdAt: performance.now() }]);
      },
    }),
    [],
  );

  // Ctrl+Z / ⌘Z отменяет последнее действие, у которого в тосте есть «Отменить».
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'KeyZ' || !(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      const last = [...items].reverse().find((t) => t.action?.undo);
      if (!last) return;
      e.preventDefault();
      last.action!.onClick();
      dismiss(last.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-28 z-toast flex flex-col items-center gap-2 px-4"
        >
          {items.map((t) => (
            <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: Item; onDismiss: () => void }) {
  const duration = item.duration ?? (item.action?.undo ? UNDO_MS : DEFAULT_MS);
  const barRef = useRef<HTMLSpanElement>(null);
  const [paused, setPaused] = useState(false);
  const latest = useRef({ onDismiss, paused });
  useLayoutEffect(() => {
    latest.current = { onDismiss, paused };
  }, [onDismiss, paused]);

  // Отсчёт идёт анимацией полоски; конец анимации — закрытие. Пауза — остановкой анимации.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const anim = bar.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], {
      duration,
      easing: 'linear',
      fill: 'forwards',
    });
    anim.onfinish = () => latest.current.onDismiss();
    if (latest.current.paused) anim.pause();
    return () => anim.cancel();
  }, [duration]);

  useEffect(() => {
    const anims = barRef.current?.getAnimations() ?? [];
    for (const a of anims) {
      if (paused) a.pause();
      else a.play();
    }
  }, [paused]);

  const Icon = item.tone === 'danger' ? CircleAlert : CheckCircle2;
  return (
    <div
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="pointer-events-auto relative flex w-full max-w-md animate-pop-in items-center gap-3 overflow-hidden rounded-lg border border-toast-line bg-toast py-3 pr-2 pl-4 text-toast-fg shadow-xl"
    >
      <Icon
        className={cn(
          'size-4 shrink-0',
          item.tone === 'danger'
            ? 'text-danger'
            : item.tone === 'success'
              ? 'text-success'
              : 'text-fg-inverse',
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Заголовок с именем не обрезается многоточием: рядом «Отменить» и Ctrl Z, места мало. */}
        <span className="line-clamp-2 text-sm font-medium">{item.title}</span>
        {item.description && <span className="truncate text-xs opacity-70">{item.description}</span>}
      </div>
      {item.action && (
        <button
          type="button"
          onClick={() => {
            item.action!.onClick();
            onDismiss();
          }}
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-semibold text-brand transition-colors duration-fast hover:bg-toast-fg/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {item.action.undo && <Undo2 className="size-4" />}
          {item.action.label}
          {item.action.undo && <Kbd className="ml-1 opacity-80">Ctrl Z</Kbd>}
        </button>
      )}
      <button
        type="button"
        aria-label="Скрыть"
        onClick={onDismiss}
        className="grid size-8 shrink-0 place-items-center rounded-md opacity-70 transition-opacity duration-fast hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <X className="size-4" />
      </button>
      <span ref={barRef} aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-brand" />
    </div>
  );
}
